import { useEffect, useRef, useState } from 'react';
import { hexToHsv, hsvToHex, normaliseHex } from '../lib/colour';
import './ColourMixer.css';

const clamp01 = (t) => Math.max(0, Math.min(1, t));

// A colour mixer: a saturation-and-brightness square, a hue rail and a hex box. It keeps its own
// hue, so a grey (which has none) can still be given one. `onChange` is given every colour as it
// is made, as "#RRGGBB". `value` is where the mixer starts, and a colour that arrives from outside
// (another swatch, the eyedropper) is taken on.
// `onUse`, when given, adds a button that applies the colour now made.
export default function ColourMixer({ value, onChange, onUse, useLabel = 'Use this colour', useDisabled = false }) {
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const [text, setText] = useState(() => String(value).toUpperCase());
  const [invalid, setInvalid] = useState(false);
  const made = useRef(String(value).toUpperCase());
  const svRef = useRef(null);
  const hueRef = useRef(null);

  useEffect(() => {
    const incoming = normaliseHex(value);
    if (incoming && incoming !== made.current) {
      made.current = incoming;
      setHsv(hexToHsv(incoming));
      setText(incoming);
      setInvalid(false);
    }
  }, [value]);

  const emit = (next) => {
    const hex = hsvToHex(next);
    made.current = hex;
    setHsv(next);
    setText(hex);
    setInvalid(false);
    onChange(hex);
  };

  const dragSV = (e) => {
    const r = svRef.current.getBoundingClientRect();
    emit({ h: hsv.h, s: clamp01((e.clientX - r.left) / r.width), v: clamp01(1 - (e.clientY - r.top) / r.height) });
  };

  const dragHue = (e) => {
    const r = hueRef.current.getBoundingClientRect();
    emit({ h: clamp01((e.clientX - r.left) / r.width) * 359.99, s: hsv.s, v: hsv.v });
  };

  // Typing a full colour applies it at once; a half-typed one only marks the box.
  const typed = (raw) => {
    setText(raw);
    const hex = normaliseHex(raw);
    if (!hex) { setInvalid(raw.trim() !== ''); return; }
    made.current = hex;
    setHsv(hexToHsv(hex));
    setInvalid(false);
    onChange(hex);
  };

  const leaveBox = () => { setText(hsvToHex(hsv)); setInvalid(false); };

  const hueHex = hsvToHex({ h: hsv.h, s: 1, v: 1 });
  const hex = hsvToHex(hsv);

  return (
    // The mousedown stops here so the palette's own handler does not steal focus from the hex box.
    <div className="colour-mixer" onMouseDown={(e) => e.stopPropagation()}>
      <div
        ref={svRef}
        className="colour-mixer__sv"
        role="slider"
        aria-label="Saturation and brightness"
        style={{ background: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, ${hueHex})` }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); dragSV(e); }}
        onPointerMove={(e) => { if (e.buttons) dragSV(e); }}
      >
        <span className="colour-mixer__thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hex }} />
      </div>
      <div
        ref={hueRef}
        className="colour-mixer__hue"
        role="slider"
        aria-label="Hue"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); dragHue(e); }}
        onPointerMove={(e) => { if (e.buttons) dragHue(e); }}
      >
        <span className="colour-mixer__thumb" style={{ left: `${(hsv.h / 360) * 100}%`, background: hueHex }} />
      </div>
      <div className="colour-mixer__row">
        <span className="colour-mixer__preview" style={{ background: hex }} />
        <input
          className={`colour-mixer__hex${invalid ? ' is-invalid' : ''}`}
          value={text}
          onChange={(e) => typed(e.target.value)}
          onBlur={leaveBox}
          maxLength={7}
          spellCheck={false}
          aria-label="Hex colour, for example #3A86FF"
        />
      </div>
      {onUse && (
        <button
          type="button"
          className="colour-mixer__use"
          disabled={useDisabled}
          onClick={() => { const h = normaliseHex(text) || hex; onUse(h); }}
        >{useLabel}</button>
      )}
    </div>
  );
}
